// Reusable Connections presentation: metadata plus an optional module-only
// project Updates destination, supplied by the loaded record's parent.
// Interpretation lives in projectConnectionsModel.js; this file is presentation.
// The cluster is linked-only; the default module also preserves name-only metadata.
import { useEffect, useRef, useState } from "react";
import { Folder, Copy, Check, ArrowUpRight } from "lucide-react";
import { T } from "../../../app/tokens.js";
import { NavRailIcon } from "../../../components/NavRailIcon.jsx";
import { interpretConnections } from "./projectConnectionsModel.js";
import "./ProjectConnections.css";

const CLUSTER_ICONS = { location: "places", lease: "leases", slack: "slack", drive: "drive" };

function ConnectionChips({ entries }) {
  const linked = entries.filter(entry => entry.href && entry.kind !== "lease");
  if (!linked.length) return null;
  return (
    <div data-testid="project-connections-compact" className="project-connections-compact"
      role="group" aria-label="Current project connections"
      style={{ "--connection-accent": T.blue }}>
      {linked.map(entry => {
        const label = `Open ${entry.title}: ${entry.label}${entry.external ? " (opens in a new tab)" : ""}`;
        return <a key={entry.kind} href={entry.href} className="project-connection-chip"
          data-testid={`connection-compact-${entry.kind}`} aria-label={label} title={label}
          style={{ color: entry.kind === "slack" ? T.purpleDeep : T.projectsHubNavy,
            background: entry.kind === "slack" ? T.purpleBg : T.hoverBg,
            borderColor: entry.kind === "slack" ? T.marketConsolidateBg : T.border,
            fontSize: T.fsXs, fontWeight: T.fwSemibold }}
          target={entry.external ? "_blank" : undefined} rel={entry.external ? "noopener noreferrer" : undefined}
          onClick={event => event.stopPropagation()}>
          <span aria-hidden="true" style={{ display: "inline-flex", flexShrink: 0, color: entry.kind === "location" ? T.green : entry.kind === "slack" ? T.purpleDeep : T.blue }}>
            <NavRailIcon itemKey={CLUSTER_ICONS[entry.kind]} size={13} />
          </span>
          <span>{entry.label}</span>
        </a>;
      })}
    </div>
  );
}

function ConnectionCluster({ entries }) {
  const linked = entries.filter(entry => entry.href);
  if (!linked.length) return null;
  return (
    <span data-testid="project-connections-cluster" className="project-connections-cluster"
      style={{ "--connection-muted": T.mid, "--connection-accent": T.blue, "--connection-border": T.border, "--connection-bg": T.card }}>
      {linked.map(entry => {
        const label = `Open ${entry.title}: ${entry.label}${entry.external ? " (opens in a new tab)" : ""}`;
        return (
          <a key={entry.kind} href={entry.href} className="project-connection-link"
            data-testid={`connection-cluster-${entry.kind}`} aria-label={label} title={label}
            target={entry.external ? "_blank" : undefined} rel={entry.external ? "noopener noreferrer" : undefined}
            onClick={event => event.stopPropagation()}>
            <span aria-hidden="true"><NavRailIcon itemKey={CLUSTER_ICONS[entry.kind]} size={14} /></span>
          </a>
        );
      })}
    </span>
  );
}

function EntryIcon({ kind }) {
  const box = { width: 28, height: 28, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: T.blueSoft, color: T.projectsHubNavy };
  return (
    <span aria-hidden="true" data-testid={`connection-icon-${kind}`} style={box}>
      {kind === "location" && <NavRailIcon itemKey="places" size={16} />}
      {kind === "lease" && <NavRailIcon itemKey="leases" size={16} />}
      {kind === "slack" && <NavRailIcon itemKey="slack" size={16} />}
      {kind === "drive" && <Folder size={16} strokeWidth={1.9} />}
    </span>
  );
}

function CodeCopy({ code }) {
  const [state, setState] = useState("idle"); // idle | copied | failed
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    clearTimeout(timer.current);
    try {
      if (!navigator?.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(code);
      setState("copied");
    } catch { setState("failed"); }
    timer.current = setTimeout(() => setState("idle"), 2400);
  };
  return (
    <div data-testid="connections-code" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: T.hoverBg, border: `1px solid ${T.border}`, borderRadius: 10 }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".06em", textTransform: "uppercase", color: T.mid }}>Project code</div>
        <div data-testid="connections-code-value" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: T.fsBase, fontWeight: T.fwBold, color: T.projectsHubNavy, overflowWrap: "anywhere" }}>{code}</div>
      </div>
      <button type="button" data-testid="connections-code-copy" onClick={copy} aria-label={`Copy project code ${code}`}
        style={{ display: "inline-flex", alignItems: "center", gap: 5, border: `1px solid ${T.border}`, background: T.card, borderRadius: 7, padding: "5px 10px", fontSize: T.fsXs, fontWeight: T.fwBold, color: state === "failed" ? T.red : T.projectsHubNavy, cursor: "pointer" }}>
        {state === "copied" ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
        {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : "Copy"}
      </button>
      <span role="status" aria-live="polite" data-testid="connections-code-status" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
        {state === "copied" ? "Project code copied" : state === "failed" ? "Could not copy the project code" : ""}
      </span>
    </div>
  );
}

function EntryRow({ entry }) {
  const linkStyle = { display: "inline-flex", alignItems: "center", gap: 3, fontSize: T.fsXs, fontWeight: T.fwBold, color: T.blue, textDecoration: "none", whiteSpace: "nowrap" };
  return (
    <li data-testid={`connection-row-${entry.kind}`} style={{ display: "flex", alignItems: "center", gap: 11, padding: "9px 2px", borderTop: `1px solid ${T.border}` }}>
      <EntryIcon kind={entry.kind} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.mid }}>{entry.title}</div>
        <div data-testid={`connection-label-${entry.kind}`} style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, overflowWrap: "anywhere" }}>{entry.label}</div>
        {entry.detail && <div style={{ fontSize: T.fsXs, color: T.mid, overflowWrap: "anywhere" }}>{entry.detail}</div>}
      </div>
      {entry.href && (entry.external ? (
        <a href={entry.href} target="_blank" rel="noopener noreferrer" data-testid={`connection-open-${entry.kind}`} aria-label={`Open ${entry.title}: ${entry.label} (opens in a new tab)`} style={linkStyle}>
          Open <ArrowUpRight size={13} aria-hidden="true" />
        </a>
      ) : (
        <a href={entry.href} data-testid={`connection-open-${entry.kind}`} aria-label={`Open ${entry.title}: ${entry.label}`} style={linkStyle}>
          Open <span aria-hidden="true">&rarr;</span>
        </a>
      ))}
    </li>
  );
}

export function ProjectConnections({ connections, updatesHref, variant = "module" }) {
  const { code, entries } = interpretConnections(connections);
  if (variant === "cluster") return <ConnectionCluster entries={entries} />;
  if (variant === "compact") return <ConnectionChips entries={entries} />;
  if (!updatesHref && !code && entries.length === 0) return null;
  return (
    <section data-testid="project-connections" data-variant={variant} aria-labelledby="project-connections-title" style={{ position: "relative", marginTop: 18 }}>
      <h3 id="project-connections-title" style={{ margin: "0 0 8px", fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.projectsHubNavy }}>Connections</h3>
      {code && <CodeCopy code={code} />}
      {(updatesHref || entries.length > 0) && (
        <ul style={{ listStyle: "none", margin: code ? "8px 0 0" : 0, padding: 0 }}>
          {updatesHref && (
            <li data-testid="connection-row-updates" style={{ borderTop: `1px solid ${T.border}` }}>
              <a href={updatesHref} data-testid="connection-open-updates" aria-label="Open Updates"
                style={{ display: "flex", alignItems: "center", gap: 11, padding: "9px 2px", color: T.blue, opacity: 1, textDecoration: "none", fontSize: T.fsSm, fontWeight: T.fwSemibold }}>
                <span aria-hidden="true" data-testid="connection-icon-updates"
                  style={{ width: 28, height: 28, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: T.blueSoft, color: T.blue }}>
                  <NavRailIcon itemKey="feed" size={16} />
                </span>
                <span style={{ flex: 1 }}>Updates</span>
                <span aria-hidden="true">&rarr;</span>
              </a>
            </li>
          )}
          {entries.map((e) => <EntryRow key={e.kind} entry={e} />)}
        </ul>
      )}
    </section>
  );
}

export default ProjectConnections;
